import { inject, Injectable } from '@angular/core';
import { RecordTableColumnParams } from "../../types/records-table.types";
import { UserPreferencesService } from "../user-preferences.service";
import {
  ColumnVisibilityContext,
  ColumnToggleParams,
  ColumnGroupVisibilityParams
} from "../../types/table-types/hidden-column.types";
import {ColumnProfile} from "../../types/user-preferences.types";

@Injectable({
  providedIn: 'root'
})
export class HiddenColumnService {
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private hiddenColumns: RecordTableColumnParams[] = [];
  private profileName: string = '';

  public toggleMultipleColumnsVisibility(columnToggleParams: ColumnToggleParams): RecordTableColumnParams[] {
    const { columnKeys, columns, profileName } = columnToggleParams;
    const columnKeysSet = new Set(columnKeys);
    this.profileName = profileName ?? '';

    columns.forEach(column => {
      if (columnKeysSet.has(column.key)) {
        column.hiddenColumn = !column.hiddenColumn;
        this.processColumnVisibility({ column, columns });
      }
    });

    return this.updateColumnsVisibility(columns);
  }

  public applyColumnProfileVisibility(columnToggleParams: ColumnToggleParams): RecordTableColumnParams[] {
    const { columnKeys, columns, profileName } = columnToggleParams;
    const columnKeysSet = new Set(columnKeys);
    this.profileName = profileName ?? '';

    columns.forEach(column => {
      column.hiddenColumn = columnKeysSet.has(column.key);
      this.processColumnVisibility({ column, columns });
      if (column.sticky) {
        column.hiddenGroup = false;
        column.hiddenColumn = false;
      }
    });

    return this.updateColumnsVisibility(columns);
  }

  private processColumnVisibility({ column, columns }: ColumnVisibilityContext): void {
    const groupId = column.columnProperties.GROUP?.id;
    if (!groupId) { return; }

    const groupColumns = columns.filter(c => c.columnProperties.GROUP?.id === groupId);
    const isGroupHidden = groupColumns.some(c => c.hiddenGroup);

    if (isGroupHidden) {
      const context: ColumnVisibilityContext = { column, columns: groupColumns };
      column.hiddenColumn ?
        this.hideColumnInGroup(context) :
        this.showColumnInGroup(context);
    }
  }

  private hideColumnInGroup({ column, columns }: ColumnVisibilityContext): void {
    if (!column.hiddenGroupPlaceholder) {
      column.hiddenGroup = false;
      return;
    }

    const nextVisibleColumn = this.findNextVisibleColumn({ column, columns });
    column.hiddenGroupPlaceholder = false;

    if (nextVisibleColumn) {
      nextVisibleColumn.hiddenGroupPlaceholder = true;
    }
  }

  private showColumnInGroup({ column, columns }: ColumnVisibilityContext): void {
    const currentPlaceholder = columns.find(c => c.hiddenGroupPlaceholder);

    currentPlaceholder ?
      this.showColumnWithPlaceholder({ column, columns, currentPlaceholder }) :
      this.showColumnAsFirst({ column, columns });
  }

  private showColumnWithPlaceholder(params: ColumnGroupVisibilityParams): void {
    const { column, columns, currentPlaceholder } = params;
    if (currentPlaceholder?.hiddenGroup) { column.hiddenGroup = true; }
    if (!this.isFirstVisibleColumn({ column, columns }) || !currentPlaceholder) { return; }

    currentPlaceholder.hiddenGroupPlaceholder = false;
    column.hiddenGroupPlaceholder = true;
  }

  private showColumnAsFirst({ column, columns }: ColumnVisibilityContext): void {
    if (!this.isFirstVisibleColumn({ column, columns })) { return; }
    column.hiddenGroupPlaceholder = column.hiddenGroup;
  }

  private findNextVisibleColumn({ column, columns }: ColumnVisibilityContext): RecordTableColumnParams | undefined {
    return columns
      .filter(c => !c.hiddenColumn && c.key !== column.key && !c.sticky)
      .sort((a, b) => a.order - b.order)
      .at(0);
  }

  private isFirstVisibleColumn({ column, columns }: ColumnVisibilityContext): boolean {
    const firstVisibleColumn = this.getFirstVisibleColumn(columns);
    return firstVisibleColumn?.key === column.key;
  }

  private getFirstVisibleColumn(columns: Array<RecordTableColumnParams>): RecordTableColumnParams | undefined {
    return columns
      .filter(c => !c.hiddenColumn && !c.sticky)
      .sort((a, b) => a.order - b.order)
      .at(0);
  }

  private updateColumnsVisibility(columns: Array<RecordTableColumnParams>): Array<RecordTableColumnParams> {
    this.hiddenColumns = columns.filter(c => c.hiddenColumn);

    if (this.hiddenColumns.length > 0) {
      this.saveHiddenColumns();
      return columns;
    }

    if (this.profileName) {
      const currentProfile = this.userPreferencesService.getUserPreferences()
        ?.columnProfiles?.find(p => p.profileName === this.profileName);
      currentProfile?.hiddenColumnKeys?.length && this.clearHiddenColumns();
    }

    return columns;
  }

  private saveHiddenColumns(): void {
    const hiddenKeys = this.hiddenColumns.map(c => c.key);
    this.saveProfileColumns(hiddenKeys);
  }

  private saveProfileColumns(hiddenKeys: Array<string>): void {
    const currentPreferences = this.userPreferencesService.getUserPreferences();
    const profiles = currentPreferences?.columnProfiles || [];
    const updatedProfiles = this.updateOrCreateProfile(profiles, hiddenKeys);

    this.userPreferencesService.saveUserPreferences({ columnProfiles: updatedProfiles });
  }

  private updateOrCreateProfile(profiles: Array<ColumnProfile>, hiddenKeys: Array<string>): Array<ColumnProfile> {
    const index = profiles.findIndex(p => p.profileName === this.profileName);
    if (index < 0) { return []; }

    const updated = [...profiles];
    updated[index] = {
      ...updated[index],
      hiddenColumnKeys: hiddenKeys
    };

    return updated;
  }

  private clearHiddenColumns(): void {
    const currentPreferences = this.userPreferencesService.getUserPreferences();
    const profiles = currentPreferences?.columnProfiles || [];
    const updatedProfiles = profiles.map(profile => ({
      ...profile,
      isActive: false,
      hiddenColumnKeys: []
    }));

    this.userPreferencesService.saveUserPreferences({ columnProfiles: updatedProfiles });
  }
}
